
import React from 'react';
import { User, ArenaConfig, UserRole } from '../../types';
import { Trophy, Settings, ListOrdered, Grid, LogOut, Calendar, History, Sun } from 'lucide-react';

// --- Color sanitizer: prevents CSS injection via theme values ---
const sanitizeColor = (color: string | undefined, fallback: string): string => {
  if (!color) return fallback;
  if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color.trim())) return color.trim();
  return fallback;
};

interface LayoutProps {
  config: ArenaConfig;
  currentUser: User | null;
  isAdmin: boolean;
  isLoggedIn: boolean;
  adminView: string;
  setAdminView: (view: any) => void;
  adminNotificationCount: number;
  onLogout: () => void;
  onShowSettings: () => void;
  children: React.ReactNode;
}

export const LightLayout: React.FC<LayoutProps> = ({
  config,
  currentUser,
  isAdmin,
  isLoggedIn,
  adminView,
  setAdminView,
  adminNotificationCount,
  onLogout,
  onShowSettings,
  children
}) => {
  const isDarkMode = config.theme?.darkMode;
  const primaryColor = sanitizeColor(config.theme?.primaryColor, '#3b82f6');
  const secondaryColor = sanitizeColor(config.theme?.secondaryColor, '#1e293b');

  const calendarConfirmedSafe = sanitizeColor(config.theme?.calendarConfirmedColor, secondaryColor);
  const calendarPendingSafe = sanitizeColor(config.theme?.calendarPendingColor, '#f97316');

  const styleVars = `
    :root {
      --color-primary: ${primaryColor};
      --color-secondary: ${secondaryColor};
      --color-primary-light: ${primaryColor}15;
      --color-calendar-confirmed: ${calendarConfirmedSafe};
      --color-calendar-pending: ${calendarPendingSafe};
    }
    
    body {
      background-color: ${isLoggedIn ? (isDarkMode ? '#0f172a' : '#ffffff') : '#ffffff'};
      color: ${isLoggedIn ? (isDarkMode ? '#e2e8f0' : '#1e293b') : '#1e293b'};
      transition: background-color 0.3s ease;
    }

    ${isDarkMode ? `
      header { background-color: #1e293b !important; border-bottom-color: #334155 !important; color: #f8fafc !important; }
      .bg-white { background-color: #334155 !important; color: #f8fafc !important; border-color: #475569 !important; }
    ` : `
      header { background-color: rgba(255, 255, 255, 0.9) !important; backdrop-filter: blur(8px); border-bottom-color: #f1f5f9 !important; }
    `}
    
    .theme-text-primary { color: var(--color-primary); }
    .theme-bg-primary { background-color: var(--color-primary); }
    .theme-bg-secondary { background-color: var(--color-secondary); }
    .theme-border-primary { border-color: var(--color-primary); }

    /* CSS Overrides für Light Template */
    ${!isDarkMode ? `
      /* Admin Menü Styling für Light Layout */
      .admin-nav-container {
        background-color: #f8fafc !important;
        border: 1px solid #e2e8f0 !important;
      }
      .admin-nav-button.active {
        background-color: #ffffff !important;
        color: var(--color-primary) !important;
        box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important;
      }
      .admin-nav-button:not(.active) {
        color: #94a3b8 !important;
      }

      /* Startmaske & Buchungsschritte */
      .bg-slate-950 { background-color: #ffffff !important; }
      .bg-slate-950 .text-white { color: #0f172a !important; }
      .bg-slate-950 .text-slate-50, .bg-slate-950 .text-slate-500, .bg-slate-950 .text-slate-400 { color: #64748b !important; }
      .bg-slate-950 .bg-white\\/5 { background-color: #f8fafc !important; border-color: #e2e8f0 !important; }
      .bg-slate-950 .border-white\\/10 { border-color: #e2e8f0 !important; }
      .bg-slate-950 .backdrop-blur-3xl { background-color: #ffffff !important; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05) !important; border: 1px solid #f1f5f9 !important; }
      
      /* Buchungs-Oberflächen */
      .bg-slate-800, .bg-slate-900 { background-color: #f8fafc !important; border: 1px solid #e2e8f0 !important; color: #1e293b !important; }
      .bg-slate-800 *, .bg-slate-900 * { color: inherit !important; }
      .bg-slate-800 .text-white, .bg-slate-900 .text-white { color: #1e293b !important; }
      .bg-slate-800 .bg-white\\/5, .bg-slate-900 .bg-white\\/5 { background-color: #ffffff !important; border-color: #cbd5e1 !important; }
    ` : ''}

    .bg-slate-950 .flex.flex-col.gap-4.text-center > .flex.items-center.gap-4.my-4,
    .bg-slate-950 .flex.flex-col.gap-4.text-center > div.flex.gap-3 {
      display: none !important;
    }
  `;

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex flex-col font-sans bg-white">
        <style>{styleVars}</style>
        {children}
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col font-sans`}>
      <style>{styleVars}</style>
      
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex flex-wrap md:flex-nowrap items-center justify-between sticky top-0 z-[80] shadow-sm animate-fade-in-down gap-y-2">
        <div className="flex items-center gap-3 order-1">
          {config.logoUrl ? (
            <img src={config.logoUrl} alt="Logo" className="w-10 h-10 object-contain rounded-xl" />
          ) : (
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white theme-bg-primary shadow-lg shadow-blue-500/20 transition-transform hover:scale-105">
                <Sun size={20} />
            </div>
          )}
          <div>
            <h1 className="font-black text-sm md:text-base text-slate-800 tracking-tighter uppercase italic">{config.name}</h1>
            {isAdmin && <span className="text-[10px] font-black theme-text-primary uppercase tracking-widest leading-none block mt-0.5">Arena Manager</span>}
          </div>
        </div>

        {isAdmin && (
          <div className={`flex order-3 md:order-2 w-full md:w-auto justify-center mt-2 md:mt-0 p-1 rounded-2xl admin-nav-container ${isDarkMode ? 'bg-slate-800' : 'bg-slate-50'}`}>
            <button
              onClick={() => setAdminView('EDITOR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-button ${
                adminView === 'EDITOR' ? 'active' : ''
              }`}
            >
              <Grid size={14} /> <span className="hidden lg:inline">Überblick</span>
            </button>
            <button
              onClick={() => setAdminView('RESERVATIONS')}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-button ${
                adminView === 'RESERVATIONS' ? 'active' : ''
              }`}
            >
              <ListOrdered size={14} /> <span className="hidden lg:inline">Buchungen</span>
              {adminNotificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white animate-pulse flex items-center justify-center text-[8px] text-white font-bold">{adminNotificationCount}</span>
              )}
            </button>
            <button
              onClick={() => setAdminView('CALENDAR')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-button ${
                adminView === 'CALENDAR' ? 'active' : ''
              }`}
            >
              <Calendar size={14} /> <span className="hidden lg:inline">Kalender</span>
            </button>
            <button
              onClick={() => setAdminView('HISTORY')}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all admin-nav-button ${
                adminView === 'HISTORY' ? 'active' : ''
              }`}
            >
              <History size={14} /> <span className="hidden lg:inline">Historie</span>
            </button>
          </div>
        )}

        <div className="flex gap-2 md:gap-4 items-center order-2 md:order-3 ml-auto md:ml-0">
          {isAdmin && (
            <button 
              onClick={onShowSettings}
              className="flex items-center justify-center w-10 h-10 rounded-xl text-slate-400 hover:bg-slate-50 transition-all border border-slate-200"
              title="Einstellungen"
            >
              <Settings size={20} /> 
            </button>
          )}

          <div className="flex items-center gap-4 pl-4 border-l border-slate-200">
            <div className="text-right hidden sm:block">
                <div className="text-xs font-black text-slate-800 uppercase tracking-tight">
                  {currentUser ? currentUser.name : 'Gast'}
                </div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">
                  {isAdmin ? 'System Admin' : 'Member'}
                </div>
            </div>
            <button 
                onClick={onLogout}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-sm ${isDarkMode ? 'bg-slate-800 text-red-400' : 'theme-bg-secondary text-white hover:opacity-95'}`}
            >
                <LogOut size={16} /> <span className="hidden md:inline">Beenden</span>
            </button>
          </div>
        </div>
      </header>

      <main className={`flex-1 relative ${adminView === 'EDITOR' && isAdmin ? 'overflow-hidden' : 'overflow-auto'} ${isDarkMode ? 'bg-slate-900 p-4 md:p-10' : 'bg-[#fafafa] p-4 md:p-10'}`}>
        {children}
      </main>
    </div>
  );
};
